import {detach, flow, types} from 'mobx-state-tree';
import {DIAGONAL_GROWTH_API} from '@/api/diagonal-growth';
import {notification} from '@/utils/notification-v2';
import {setItem} from '@/utils/localStore';

const historicalPositionModal = types.model({
  position: types.maybeNull(types.number),
  searchedAt: types.maybeNull(types.string),
});

const CampaignKeywordsModel = types.model({
  searchedAt: types.maybeNull(types.string),
  bestPosition: types.maybeNull(types.number),
  historicalPosition: types.maybeNull(types.array(historicalPositionModal)),
  clickedUrl: types.maybeNull(types.string),
  clicks: types.maybeNull(types.number),
  keyword: types.maybeNull(types.string),
  searches: types.maybeNull(types.number),
  lastSearchedAt: types.maybeNull(types.string),
  worstPosition: types.maybeNull(types.number),
  positionChange: types.maybeNull(types.number),
  previouseAvgPosition: types.maybeNull(types.number),
  latestAvgPosition: types.maybeNull(types.number),
  status: types.maybeNull(types.string),
});

const KeywordDistributionModel = types.model({
  lable: types.maybeNull(types.string),
  keywords: types.maybeNull(types.array(types.string)),
  keywordsCount: types.maybeNull(types.number),
});

const KeywordSearchHistoryModel = types.model({
  keyword: types.maybeNull(types.string),
  avgPosition: types.maybeNull(types.number),
  searchedAt: types.maybeNull(types.string),
});

const CampaignReportModal = types.model({
  currentAvgPosition: types.maybeNull(types.number),
  previousAvgPosition: types.maybeNull(types.number),
  totalOrganicClicks: types.maybeNull(types.number),
  tableData: types.maybeNull(types.array(CampaignKeywordsModel)),
});

const historicalKeywordDistributionModal = types.model({
  searchedAt: types.maybeNull(types.string),
  serp13: types.maybeNull(types.number),
  serp47: types.maybeNull(types.number),
  serp810: types.maybeNull(types.number),
  serp11: types.maybeNull(types.number),
});

const DiagonalGrowthWebsiteDetailModel = types.model({
  id: types.number,
  host: types.maybeNull(types.string),
  keywords: types.maybeNull(types.array(types.string)),
  createdAt: types.maybeNull(types.string),
  historicalKeywordDistribution: types.maybeNull(types.array(historicalKeywordDistributionModal)),
  keywordsCount: types.maybeNull(types.number),
  organicTargetDomain: types.maybeNull(types.string),
  numberOfDailySearches: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  improvedKeywordsCount: types.maybeNull(types.number),
  declinedKeywordsCount: types.maybeNull(types.number),
  campaignStartAt: types.maybeNull(types.string),
  campaignReport: types.maybeNull(CampaignReportModal),
  keywordDistribution: types.maybeNull(types.array(KeywordDistributionModel)),
  keywordSearchHistory: types.maybeNull(types.array(KeywordSearchHistoryModel)),
  maxOrganicPageResults: types.maybeNull(types.number),
});

const KeywordsInTopModal = types.model({
  currentTop3Keywords: types.maybeNull(types.number),
  previousTop3Keywords: types.maybeNull(types.number),
});

const PositionReportModal = types.model({
  currentAvgPosition: types.maybeNull(types.union(types.float, types.number)),
  previousAvgPosition: types.maybeNull(types.union(types.float, types.number)),
});

const HistoricalPositionReportModal = types.model({
  avgPosition: types.maybeNull(types.union(types.float, types.number)),
  searchedAt: types.maybeNull(types.string),
});

const KeywordAvgPositionModal = types.model({
  averagePosition: types.maybeNull(types.union(types.float, types.number)),
  searchedAt: types.maybeNull(types.string),
});

const historicalKeywordsInTop3Modal = types.model({
  top3Keywords: types.maybeNull(types.number),
  searchedAtDate: types.maybeNull(types.string),
});

const DiagonalGrowthWebsitesList = types.model({
  id: types.number,
  name: types.maybeNull(types.string),
  keywords: types.maybeNull(types.array(types.string)),
  organicTargetDomain: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  numberOfDailySearches: types.maybeNull(types.number),
  keywordsInTop3: types.maybeNull(types.union(KeywordsInTopModal, types.array(KeywordsInTopModal))),
  positionReport: types.maybeNull(PositionReportModal),
  historicalPositionReport: types.maybeNull(types.array(HistoricalPositionReportModal)),
  historicalKeywordsInTop3: types.maybeNull(types.array(historicalKeywordsInTop3Modal)),
  historicalKeywordDistribution: types.maybeNull(types.array(historicalKeywordDistributionModal)),
  lastSyncedAt: types.maybeNull(types.string),
  organicClicks: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  improvedKeywordsCount: types.maybeNull(types.number),
  declinedKeywordsCount: types.maybeNull(types.number),
});

const DiagonalGrowthReferralTrafficList = types.model({
  id: types.number,
  targetDomain: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  urls: types.maybeNull(types.array(types.string)),
  urlsCount: types.maybeNull(types.number),
  organicClicks: types.maybeNull(types.number),
  dailyClicks: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  historicalClicksReport: types.maybeNull(types.array(types.model({
    clicks: types.maybeNull(types.number),
    searchedAt: types.maybeNull(types.string),
  }))),
});

const DiagonalGrowthReferralList = types.model({
  count: types.maybeNull(types.number),
  activeCampaignsCount: types.maybeNull(types.number),
  inactiveCampaignsCount: types.maybeNull(types.number),
  next: types.maybeNull(types.string),
  pageSize: types.maybeNull(types.number),
  totalPages: types.maybeNull(types.number),
  previous: types.maybeNull(types.string),
  results: types.maybeNull(types.array(DiagonalGrowthReferralTrafficList)),
});

const DiagonalGrowthWebsites = types.model({
  count: types.maybeNull(types.number),
  activeCampaignsCount: types.maybeNull(types.number),
  inactiveCampaignsCount: types.maybeNull(types.number),
  next: types.maybeNull(types.string),
  pageSize: types.maybeNull(types.number),
  totalPages: types.maybeNull(types.number),
  previous: types.maybeNull(types.string),
  results: types.maybeNull(types.array(DiagonalGrowthWebsitesList)),
});
const GmbKeywordDistributionModal = types.model({
  searchedAt: types.maybeNull(types.string),
  serp13: types.maybeNull(types.number),
  serp45: types.maybeNull(types.number),
  serp67: types.maybeNull(types.number),
  serp810: types.maybeNull(types.number),
  serp11: types.maybeNull(types.number),
});
const GmbKeywordsInTopModal = types.model({
  currentTop3Keywords: types.maybeNull(types.number),
  previousTop3Keywords: types.maybeNull(types.number),
});
const GmbPositionReportModal = types.model({
  currentAvgPosition: types.maybeNull(types.union(types.float, types.number)),
  previousAvgPosition: types.maybeNull(types.union(types.float, types.number)),
});
const GmbHistoricalKeywordsInTop3Modal = types.model({
  top3Keywords: types.maybeNull(types.number),
  searchedAtDate: types.maybeNull(types.string),
});
const GmbHistoricalPositionReportModal = types.model({
  avgPosition: types.maybeNull(types.union(types.float, types.number)),
  searchedAt: types.maybeNull(types.string),
});
const GmbHistoricalPositionModel = types.model({
  position: types.maybeNull(types.number),
  searched_at: types.maybeNull(types.string),
});
const GmbTableDataModel = types.model({
  keyword: types.maybeNull(types.string),
  clicks: types.maybeNull(types.number),
  searches: types.maybeNull(types.number),
  historicalPosition: types.maybeNull(types.array(GmbHistoricalPositionModel)),
  positionChange: types.maybeNull(types.number),
  previouseAvgPosition: types.maybeNull(types.number),
  latestAvgPosition: types.maybeNull(types.number),
  clickedUrl: types.maybeNull(types.string),
  bestPosition: types.maybeNull(types.number),
  worstPosition: types.maybeNull(types.number),
  lastSearchedAt: types.maybeNull(types.string),
  searchedAt: types.maybeNull(types.string),
});
const GmbCampaignKeywordModel = types.model({
  totalOrganicClicks: types.maybeNull(types.number),
  currentAvgPosition: types.maybeNull(types.number),
  previousAvgPosition: types.maybeNull(types.number),
  tableData: types.maybeNull(types.array(GmbTableDataModel)),
});

const GmbKeywordSearchHistoryModel = types.model({
  searchedAt: types.maybeNull(types.string),
  position: types.maybeNull(types.number),
  keyword: types.maybeNull(types.string),
});

const SingleGoogleMyBusiness = types.model({
  id: types.number,
  createdAt: types.maybeNull(types.string),
  keywords: types.maybeNull(types.array(types.string)),
  businessName: types.maybeNull(types.string),
  localSeoProjectId: types.maybeNull(types.number),
  heatMapEndpoint: types.maybeNull(types.string),
  declinedKeywordsCount: types.maybeNull(types.number),
  improvedKeywordsCount: types.maybeNull(types.number),
  googleUrl: types.maybeNull(types.string),
  isActive: types.maybeNull(types.boolean),
  rating: types.maybeNull(types.number),
  reviews: types.maybeNull(types.number),
  historicalKeywordDistribution: types.maybeNull(types.array(GmbKeywordDistributionModal)),
  organicClicks: types.maybeNull(types.number),
  keywordsInTop3: types.maybeNull(GmbKeywordsInTopModal),
  positionReport: types.maybeNull(GmbPositionReportModal),
  historicalKeywordsInTop3: types.maybeNull(types.array(GmbHistoricalKeywordsInTop3Modal)),
  historicalPositionReport: types.maybeNull(types.array(GmbHistoricalPositionReportModal)),
  campaignKeywords: types.maybeNull(GmbCampaignKeywordModel),
  keywordSearchHistory: types.maybeNull(types.array(GmbKeywordSearchHistoryModel)),
  numberOfDailySearches: types.maybeNull(types.number),
  radiusMilesFromCenter: types.maybeNull(types.string),
  gridSize: types.maybeNull(types.number),
  coordinates: types.maybeNull(types.array(types.number)),
  maxOrganicPageResults: types.maybeNull(types.number),
  numberOfSearchResults: types.maybeNull(types.number),
});
const GoogleMyBusiness = types.model({
  count: types.maybeNull(types.number),
  activeCampaignsCount: types.maybeNull(types.number),
  inactiveCampaignsCount: types.maybeNull(types.number),
  next: types.maybeNull(types.string),
  previous: types.maybeNull(types.string),
  results: types.maybeNull(types.array(SingleGoogleMyBusiness)),
  totalPages: types.maybeNull(types.number),
  pageSize: types.maybeNull(types.number),
});
const HistoricalPositionsModel = types.model({
  searchedAt: types.maybeNull(types.string),
  averagePosition: types.maybeNull(types.number),
});

const GoogleMyLocationData = types.model({
  dataCid: types.maybeNull(types.string),
  webResultsLink: types.maybeNull(types.string),
  address: types.maybeNull(types.string),
  googleUrl: types.maybeNull(types.string),
  lat: types.maybeNull(types.number),
  lng: types.maybeNull(types.number),
  value: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
});

const GmbKeywordStatsModel = types.model({
  keyword: types.maybeNull(types.string),
  totalSearches: types.maybeNull(types.number),
  totalClicks: types.maybeNull(types.number),
  bestPosition: types.maybeNull(types.number),
  worstPosition: types.maybeNull(types.number),
  recentSearched: types.maybeNull(types.string),
  change: types.maybeNull(types.number),
  currentPosition: types.maybeNull(types.number),
  previousPosition: types.maybeNull(types.number),
  historicalPositions: types.maybeNull(types.array(HistoricalPositionsModel)),
});
const KeywordPositionHistory = types.model({
  searchedAt: types.maybeNull(types.string),
  serp13: types.maybeNull(types.number),
  serp45: types.maybeNull(types.number),
  serp67: types.maybeNull(types.number),
  serp810: types.maybeNull(types.number),
  serp11: types.maybeNull(types.number),
});
export const RecentKeywordCountModel = types.model({
  searchedAt: types.maybeNull(types.string),
  serp13: types.maybeNull(types.number),
  serp45: types.maybeNull(types.number),
  serp67: types.maybeNull(types.number),
  serp810: types.maybeNull(types.number),
  serp11: types.maybeNull(types.number),
});
const KeywordPositionHistoryDataModel = types.model({
  keywordPositionHistory: types.maybeNull(types.array(KeywordPositionHistory)),
  recentKeywordCount: types.maybeNull(types.union(RecentKeywordCountModel, types.number)),
});
const GridCoordinatesModal = types.model({
  lon: types.maybeNull(types.union(types.number, types.float)),
  lat: types.maybeNull(types.union(types.number, types.float)),
});
const latestSnapshotModal = types.model({
  date: types.maybeNull(types.string),
  gmbWorstPosition: types.maybeNull(types.union(types.number, types.float)),
  gmbBestPosition: types.maybeNull(types.union(types.number, types.float)),
  gmbAveragePosition: types.maybeNull(types.union(types.number, types.float)),
  gmbCenterPosition: types.maybeNull(types.union(types.number, types.float)),
});
const keywordMetricsModal = types.model({
  keyword: types.maybeNull(types.string),
  gmbWorstPosition: types.maybeNull(types.union(types.number, types.float)),
  gmbBestPosition: types.maybeNull(types.union(types.number, types.float)),
  gmbAveragePosition: types.maybeNull(types.union(types.number, types.float)),
  gmbCenterPosition: types.maybeNull(types.union(types.number, types.float)),
});
const HeatMapResultsModal = types.model({
  id: types.maybeNull(types.number),
  keyword: types.maybeNull(types.string),
  updateFrequencyDays: types.maybeNull(types.number),
  spacing: types.maybeNull(types.union(types.number, types.float)),
  gridCoords: types.maybeNull(types.array(GridCoordinatesModal)),
  status: types.maybeNull(types.string),
  latestSnapshot: types.maybeNull(latestSnapshotModal),
  latestGird: types.maybeNull(types.number),
  gridSize: types.maybeNull(types.string),
  radiusMiles: types.maybeNull(types.union(types.number, types.float)),
  feedback: types.maybeNull(types.model({reviews: types.maybeNull(types.union(types.number, types.float)), rating: types.maybeNull(types.union(types.number, types.float))})),
  keywordMetrics: types.maybeNull(keywordMetricsModal),
});
const HeatMapModal = types.model({
  count: types.maybeNull(types.number),
  next: types.maybeNull(types.string),
  previous: types.maybeNull(types.string),
  results: types.maybeNull(types.array(HeatMapResultsModal)),
});
const serpsModel = types.model({
  address: types.maybeNull(types.string),
  allCategories: types.maybeNull(types.array(types.maybeNull(types.string))),
  category: types.maybeNull(types.string),
  dataCid: types.maybeNull(types.string),
  dataId: types.maybeNull(types.string),
  gpsCoordinates: types.maybeNull(types.model({
    longitude: types.maybeNull(types.number),
    latitude: types.maybeNull(types.number),
  })),
  link: types.maybeNull(types.string),
  // opening_hours
  phone: types.maybeNull(types.string),
  placeId: types.maybeNull(types.string),
  position: types.maybeNull(types.number),
  price: types.maybeNull(types.union(types.string, types.number)),
  priceParsed: types.maybeNull(types.number),
  rating: types.maybeNull(types.number),
  reviews: types.maybeNull(types.number),
  thumbnail: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  webResultsLink: types.maybeNull(types.string),
  googleUrl: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  opening_hours: types.maybeNull(types.model({
    current: types.maybeNull(types.string),
    per_day: types.maybeNull(types.array(types.model({
      name: types.maybeNull(types.string),
      value: types.maybeNull(types.string),
    }))),
  })),
  priceDescription: types.maybeNull(types.string),
});
const HeatMapDetailResultsDataModal = types.model({
  keyword: types.maybeNull(types.string),
  gmbPosition: types.maybeNull(types.union(types.number, types.float)),
  location: types.maybeNull(types.model({lon: types.maybeNull(types.union(types.number, types.float)), lat: types.maybeNull(types.union(types.number, types.float))})),
  date: types.maybeNull(types.string),
  serps: types.maybeNull(types.array(serpsModel)),
});


const HeatMapDetailResultsModal = types.model({
  date: types.maybeNull(types.string),
  data: types.maybeNull(types.array(HeatMapDetailResultsDataModal)),
});

const HeatMapDetailModal = types.model({
  results: types.maybeNull(types.array(HeatMapDetailResultsModal)),
  gridSize: types.maybeNull(types.string),
  lastProcessedAt: types.maybeNull(types.string),
  radiusMiles: types.maybeNull(types.float),
  status: types.maybeNull(types.string),
});

const ReferralTrafficReportModel = types.model({
  directUrl: types.maybeNull(types.string),
  totalClicks: types.maybeNull(types.number),
  lastClickedAt: types.maybeNull(types.string),
});
const ReferralTrafficDetailData = types.model({
  id: types.maybeNull(types.number),
  host: types.maybeNull(types.string),
  targetDomain: types.maybeNull(types.string),
  isActive: types.maybeNull(types.boolean),
  campaignStartAt: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  organicClicks: types.maybeNull(types.number),
  dailyClicks: types.maybeNull(types.number),
  campaignReport: types.maybeNull(types.array(ReferralTrafficReportModel)),
  historicalClicksReport: types.maybeNull(types.array(types.model({
    clicks: types.maybeNull(types.number),
    searchedAt: types.maybeNull(types.string),
  }))),
});
const KeywordDistributionChartDataModel = types.model({
  recentKeywordCount: types.maybeNull(types.array(types.model(
    {
      keywords: types.maybeNull(types.array(types.string)),
      keywordsCount: types.maybeNull(types.number),
      lable: types.string,
    }))),
});

export const DiagonalGrowthStore = types.model({
  websiteDetailData: types.maybeNull(DiagonalGrowthWebsiteDetailModel),
  websiteDetailLoading: types.boolean,
  createCampaignLoader: types.boolean,
  createCampaignGmbLoader: types.boolean,
  createReferralCampaignLoader: types.boolean,
  updateReferralCampaignLoader: types.boolean,
  updateCampaignLoader: types.boolean,
  googleMyBusinessList: types.maybeNull(GoogleMyBusiness),
  diagonalGrowthGmbListLoader: types.boolean,
  gmbDetailLoading: types.boolean,
  heatMapLocationsLoader: types.boolean,
  googleMyLocationObj: types.maybeNull(GoogleMyLocationData),
  gmbDetailData: types.maybeNull(SingleGoogleMyBusiness),
  gmbKeywordStats: types.maybeNull(types.array(GmbKeywordStatsModel)),
  loadingGmbKeywordStats: types.boolean,
  keywordPositionHistoryData: types.maybeNull(KeywordPositionHistoryDataModel),
  loadingKeywordPositionHistoryData: types.boolean,
  keywordDistributionChartData: types.maybeNull(KeywordDistributionChartDataModel),
  loadingKeywordDistributionChartData: types.boolean,
  diagonalGrowthWebsitesList: types.maybeNull(DiagonalGrowthWebsites),
  diagonalGrowthWebsitesListLoader: types.boolean,
  pageNumber: types.number,
  pageSize: types.number,
  websiteUrl: types.string,
  campaignType: types.string,
  showCampaignConfigModal: types.boolean,
  referralTrafficList: types.maybeNull(DiagonalGrowthReferralList),
  referralTrafficLoading: types.boolean,
  referralTrafficDetail: types.maybeNull(ReferralTrafficDetailData),
  referralTrafficDetailLoading: types.boolean,
  singleReferralTrafficDetail: types.maybeNull(DiagonalGrowthReferralTrafficList),
  loadingSingleReferralTrafficDetail: types.boolean,
  loadingLocalSeoDetail: types.maybeNull(types.boolean),
  heatMapData: types.maybeNull(HeatMapModal),
  heatMapDetail: types.maybeNull(HeatMapDetailModal),
  KeywordAvgPosition: types.maybeNull(types.array(KeywordAvgPositionModal)),
  updateStatusLoading: types.boolean,
  campaignId: types.maybeNull(types.string),
  periodStart: types.maybeNull(types.string),
  periodEnd: types.maybeNull(types.string),
  gmbPageNumber: types.maybeNull(types.number),
  gmbPageSize: types.maybeNull(types.number),
  drtPageNumber: types.maybeNull(types.number),
  drtPageSize: types.maybeNull(types.number),
  loadingAvailableDates: types.boolean,
  availableDates: types.maybeNull(types.array(types.model({
    date: types.maybeNull(types.string),
  }))),
}).actions(self => {
  const setParams = (page: number, pageSize: number) => {
    self.pageNumber = page;
    self.pageSize = pageSize;
    loadDiagonalGrowthWebsitesList();
  };
  const setGmbParams = (page: number, pageSize: number) => {
    self.gmbPageNumber = page;
    self.gmbPageSize = pageSize;
    loadDiagonalGrowthGmbList();
  };
  const setDrtParams = (page: number, pageSize: number) => {
    self.drtPageNumber = page;
    self.drtPageSize = pageSize;
    loadDiagonalGrowthReferralTrafficList();
  };
  const loadWebsiteDetail = flow(function* (id, periodStart, periodEnd) {
    self.websiteDetailLoading = true;
    if (id) {
      self.campaignId = id;
      self.periodStart = periodStart ?? self.periodStart;
      self.periodEnd = periodEnd ?? self.periodEnd;
    }
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getDiagonalGrowthWebsite(params);
      if (self.websiteDetailData) {
        detach(self.websiteDetailData);
      }
      if (response) {
        self.websiteDetailData = response;
      }
      return response;
    } catch (e) {
      self.websiteDetailData = null;
      return Promise.reject(e);
    } finally {
      self.websiteDetailLoading = false;
    }
  });
  const loadDiagonalGrowthReferralTrafficList = flow(function* () {
    self.referralTrafficLoading = true;
    try {
      const params = {
        pageNumber: self.drtPageNumber ?? 1,
        pageSize: self.drtPageSize ?? 10,
      };
      const response = yield DIAGONAL_GROWTH_API.getReferralTrafficList(params);
      if (self.referralTrafficList) {
        detach(self.referralTrafficList);
      }
      if (response) {
        self.referralTrafficList = response;
      }
      return response.results;
    } catch (e) {
      self.referralTrafficLoading = false;
      return Promise.reject(e);
    } finally {
      self.referralTrafficLoading = false;
    }
  });
  const loadDiagonalGrowthWebsitesList = flow(function* () {
    self.diagonalGrowthWebsitesListLoader = true;
    try {
      const params = {
        pageNumber: self.pageNumber ?? 1,
        pageSize: self.pageSize ?? 10,
      };
      const response = yield DIAGONAL_GROWTH_API.getDiagonalGrowthWebsites(params);
      if (self.diagonalGrowthWebsitesList) {
        detach(self.diagonalGrowthWebsitesList);
      }
      if (response) {
        self.diagonalGrowthWebsitesList = response;
      }
      return response.results;
    } catch (e) {
      self.diagonalGrowthWebsitesListLoader = false;
      return Promise.reject(e);
    } finally {
      self.diagonalGrowthWebsitesListLoader = false;
    }
  });
  const getKeywordPositionAvg = flow(function* (id, periodStart, periodEnd) {
    self.gmbDetailLoading = true;
    if (id) {
      self.campaignId = id;
      self.periodStart = periodStart ?? self.periodStart;
      self.periodEnd = periodEnd ?? self.periodEnd;
    }
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getKeywordPositionAvg(params);
      if (self.KeywordAvgPosition) {
        detach(self.KeywordAvgPosition);
      }
      if (response) {
        self.KeywordAvgPosition = response?.keywordAvgPosition;
      }
      return response;
    } catch (e) {
      self.KeywordAvgPosition = null;
      return Promise.reject(e);
    } finally {
      self.gmbDetailLoading = false;
    }
  });
  const loadGoogleMyBusinessDetail = flow(function* (id) {
    self.gmbDetailLoading = true;
    if (id) {
      self.campaignId = id;
    }
    const params = {
      id: id,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getGoogleMyBusiness(params);
      if (self.gmbDetailData) {
        detach(self.gmbDetailData);
      }
      if (response) {
        self.gmbDetailData = response;
        if (response?.localSeoProjectId) {
          getHeatMapData(response?.localSeoProjectId);
        }
      }
      return response;
    } catch (e) {
      self.gmbDetailData = null;
      return Promise.reject(e);
    } finally {
      self.gmbDetailLoading = false;
    }
  });
  const loadReferralTrafficDetail = flow(function* (id, periodStart, periodEnd) {
    self.referralTrafficDetailLoading = true;
    if (id) {
      self.campaignId = id;
      self.periodStart = periodStart ?? self.periodStart;
      self.periodEnd = periodEnd ?? self.periodEnd;
    }
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getReferralTrafficDetail(params);
      if (self.referralTrafficDetail) {
        detach(self.referralTrafficDetail);
      }
      if (response) {
        self.referralTrafficDetail = response;
      }
      return response;
    } catch (e) {
      self.referralTrafficDetail = null;
      return Promise.reject(e);
    } finally {
      self.referralTrafficDetailLoading = false;
    }
  });
  const loadSingleReferralTrafficDetail = flow(function* (id) {
    self.loadingSingleReferralTrafficDetail = true;
    if (id) {
      self.campaignId = String(id);
    }
    try {
      const response = yield DIAGONAL_GROWTH_API.loadSingleReferralTrafficDetail(id);
      if (self.singleReferralTrafficDetail) {
        detach(self.singleReferralTrafficDetail);
      }
      if (response) {
        self.singleReferralTrafficDetail = response;
      }
    } catch (e) {
      self.referralTrafficDetail = null;
      return Promise.reject(e);
    } finally {
      self.loadingSingleReferralTrafficDetail = false;
    }
  });
  const getHeatMapData = flow(function* (id) {
    self.loadingLocalSeoDetail = true;
    const params = {
      id: id,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getHeatMapData(params);
      if (self.heatMapData) {
        detach(self.heatMapData);
      }
      if (response) {
        self.heatMapData = response;
      }
      return response;
    } catch (e) {
      self.heatMapData = null;
      return Promise.reject(e);
    } finally {
      self.loadingLocalSeoDetail = false;
    }
  });
  const getHeatMapDetail = flow(function* (id, gridId, date?: string) {
    self.gmbDetailLoading = true;
    const params = {
      id: id,
      gridId: gridId,
      date,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getHeatMapDataDetail(params);
      return response;
    } catch (e) {
      self.gmbDetailData = null;
      return Promise.reject(e);
    } finally {
      self.gmbDetailLoading = false;
    }
  });
  const loadDiagonalGrowthGmbList = flow(function* () {
    self.diagonalGrowthGmbListLoader = true;
    try {
      const params = {
        pageNumber: self.gmbPageNumber ?? 1,
        pageSize: self.gmbPageSize ?? 10,
      };
      const response = yield DIAGONAL_GROWTH_API.getGoogleMyBusinessList(params);
      if (self.googleMyBusinessList) {
        detach(self.googleMyBusinessList);
      }
      if (response) {
        self.googleMyBusinessList = response;
      }
      return response.results;
    } catch (e) {
      self.diagonalGrowthGmbListLoader = false;
      return Promise.reject(e);
    } finally {
      self.diagonalGrowthGmbListLoader = false;
    }
  });
  const getGmbKeywordStats = flow(function* (id, periodStart, periodEnd) {
    self.loadingGmbKeywordStats = true;
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getGmbKeywordStats(params);
      if (response) {
        self.gmbKeywordStats = response;
      }
      return response;
    } catch (e) {
      self.gmbKeywordStats = null;
      return Promise.reject(e);
    } finally {
      self.loadingGmbKeywordStats = false;
    }
  });
  const getKeywordPositionHistory = flow(function* (id, periodStart, periodEnd) {
    self.loadingKeywordPositionHistoryData = true;
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getKeywordPositionHistory(params);
      if (response) {
        self.keywordPositionHistoryData = response;
      }
      return response;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      self.loadingKeywordPositionHistoryData = false;
    }
  });
  const getKeywordDistributionChartData = flow(function* (id, periodStart, periodEnd) {
    self.loadingKeywordDistributionChartData = true;
    const params = {
      id: id,
      periodStart: periodStart ?? self.periodStart,
      periodEnd: periodEnd ?? self.periodEnd,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getKeywordDistributionChartData(params);
      if (response) {
        self.keywordDistributionChartData = response;
      }
      return response;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      self.loadingKeywordDistributionChartData = false;
    }
  });
  const createCampaign = flow(function* (payload) {
    self.createCampaignLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.createDiagonalGrowthWebsiteCampaign(payload);
      if (response) {
        notification.success('Success!', 'Created Successfully');
        setWebsiteUrl('');
        loadDiagonalGrowthWebsitesList();
      }
    } catch (e) {
      self.createCampaignLoader = false;
      return Promise.reject(e);
    } finally {
      self.createCampaignLoader = false;
    }
  });
  const createGmbCampaign = flow(function* (payload) {
    self.createCampaignGmbLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.createDiagonalGrowthGmbCampaign(payload);
      if (response) {
        notification.success('Success!', 'Created Successfully');
        setGoogleMyLocationObj(null);
        loadDiagonalGrowthGmbList();
      }
    } catch (e) {
      self.createCampaignGmbLoader = false;
      return Promise.reject(e);
    } finally {
      self.createCampaignGmbLoader = false;
    }
  });
  const createReferralCampaign = flow(function* (payload) {
    self.createReferralCampaignLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.createReferralCampaign(payload);
      if (response) {
        notification.success('Success!', 'Created Successfully');
        setWebsiteUrl('');
        loadDiagonalGrowthReferralTrafficList();
      }
    } catch (e) {
      self.createReferralCampaignLoader = false;
      return Promise.reject(e);
    } finally {
      self.createReferralCampaignLoader = false;
    }
  });
  const updateReferralCampaign = flow(function* (id, payload) {
    self.updateReferralCampaignLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.updateReferralCampaign(id, payload);
      if (response) {
        notification.success('Updated!', 'Updated successfully');
        loadReferralTrafficDetail(self.campaignId, self.periodStart, self.periodEnd);
      }
    } catch (e) {
      notification.error('Failed!', 'Failed to update campaign.');
      self.updateReferralCampaignLoader = false;
      return Promise.reject(e);
    } finally {
      self.updateReferralCampaignLoader = false;
    }
  });
  const deleteCampaign = flow(function* (id) {
    try {
      yield DIAGONAL_GROWTH_API.deleteDiagonalGrowthWebsiteCampaign(id);
      notification.success('Delete!', 'Campaign Deleted Successfully');
      setWebsiteUrl('');
      loadDiagonalGrowthWebsitesList();
      return {deleted: true};
    } catch (e) {
      return Promise.reject(e);
    }
  });
  const deleteGmbCampaign = flow(function* (id) {
    try {
      yield DIAGONAL_GROWTH_API.deleteDiagonalGrowthGmbCampaign(id);
      notification.success('Delete!', 'Campaign Deleted Successfully');
      setGoogleMyLocationObj(null);
      loadDiagonalGrowthGmbList();
      return {deleted: true};
    } catch (e) {
      return Promise.reject(e);
    }
  });
  const deleteDrtCampaign = flow(function* (id) {
    try {
      yield DIAGONAL_GROWTH_API.deleteDiagonalGrowthReferralCampaign(id);
      notification.success('Delete!', 'Campaign Deleted Successfully');
      setWebsiteUrl('');
      loadDiagonalGrowthReferralTrafficList();
      return {deleted: true};
    } catch (e) {
      return Promise.reject(e);
    }
  });
  const updateCampaign = flow(function* (id, payload) {
    self.updateCampaignLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.updateDiagonalGrowthWebsiteCampaign(id, payload);
      if (response) {
        notification.success('Updated!', 'Updated successfully');
        loadWebsiteDetail(self.campaignId, self.periodStart, self.periodEnd);
      }
    } catch (e) {
      notification.error('Failed!', 'Failed to update campaign.');
      self.updateCampaignLoader = false;
      return Promise.reject(e);
    } finally {
      self.updateCampaignLoader = false;
    }
  });

  const updateGmbCampaign = flow(function* (id, payload) {
    self.updateCampaignLoader = true;
    try {
      const response = yield DIAGONAL_GROWTH_API.updateDiagonalGrowthGmbCampaign(id, payload);
      if (response) {
        notification.success('Updated!', 'Updated successfully');
        loadGoogleMyBusinessDetail(self.campaignId);
      }
    } catch (e) {
      notification.error('Failed!', 'Failed to update campaign.');
      self.updateCampaignLoader = false;
      return Promise.reject(e);
    } finally {
      self.updateCampaignLoader = false;
    }
  });

  const activateCampaignStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.activateCampaignStatus(id);
      notification.success('Updated!', 'Campaign Activated Successfully');
      loadWebsiteDetail(self.campaignId, self.periodStart, self.periodEnd);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });

  const activateGmbCampaignStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.activateGmbCampaignStatus(id);
      notification.success('Updated!', 'Campaign Activated Successfully');
      loadGoogleMyBusinessDetail(self.campaignId);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });
  const activateDrtStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.activateDrtStatus(id);
      notification.success('Updated!', 'Campaign Activated Successfully');
      loadReferralTrafficDetail(self.campaignId, self.periodStart, self.periodEnd);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });

  const deactivateDrtStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.deactivateDrtStatus(id);
      notification.success('Updated!', 'Campaign Deactivated Successfully');
      loadReferralTrafficDetail(self.campaignId, self.periodStart, self.periodEnd);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });

  const deactivateGmbCampaignStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.deactivateGmbCampaignStatus(id);
      notification.success('Updated!', 'Campaign Activated Successfully');
      loadGoogleMyBusinessDetail(self.campaignId);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });

  const deactivateCampaignStatus = flow(function* (id) {
    self.updateStatusLoading = true;
    try {
      yield DIAGONAL_GROWTH_API.deactivateCampaignStatus(id);
      notification.success('Updated!', 'Campaign Deactivated Successfully');
      loadWebsiteDetail(self.campaignId, self.periodStart, self.periodEnd);
    } catch (e) {
      self.updateStatusLoading = false;
      notification.error('Failed!', 'Failed to update campaign status.');
    } finally {
      self.updateStatusLoading = false;
    }
  });

  const setCreateCampaignLoader = (value: boolean) => {
    self.createCampaignLoader = value;
  };
  const setUpdateCampaignLoader = (value: boolean) => {
    self.updateCampaignLoader = value;
  };
  const setGmbDetailData = val => self.gmbDetailData = val;
  const setCampaignType = val => self.campaignType = val;
  const setHeatMapData = val => {
    if (self.heatMapData) {
      detach(self.heatMapData);
    }
    self.heatMapData = val;
  };
  const setWebsiteUrl = value => self.websiteUrl = value;
  const setGoogleMyLocationObj = value => self.googleMyLocationObj = value;
  const setShowCampaignConfigModal = value => self.showCampaignConfigModal = value;
  const setPeriodStart = value => {
    setItem('campaignStartDate', value);
    self.periodStart = value;
  };

  const getAvailableDates = flow(function* (id, gridId) {
    self.loadingAvailableDates = true;
    const params = {
      id: id,
      gridId,
    };
    try {
      const response = yield DIAGONAL_GROWTH_API.getAvailableDates(params);
      self.availableDates = response.availableDates;
      return response;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      self.loadingAvailableDates = false;
    }
  });

  return {
    loadWebsiteDetail,
    setHeatMapData,
    createCampaign,
    updateCampaign,
    setCreateCampaignLoader,
    setUpdateCampaignLoader,
    loadDiagonalGrowthWebsitesList,
    activateCampaignStatus,
    deactivateCampaignStatus,
    deleteCampaign,
    setParams,
    setWebsiteUrl,
    setShowCampaignConfigModal,
    setPeriodStart,
    loadDiagonalGrowthGmbList,
    loadGoogleMyBusinessDetail,
    updateGmbCampaign,
    getKeywordPositionAvg,
    getGmbKeywordStats,
    getHeatMapData,
    getKeywordPositionHistory,
    getKeywordDistributionChartData,
    getHeatMapDetail,
    deleteGmbCampaign,
    setGoogleMyLocationObj,
    createGmbCampaign,
    activateGmbCampaignStatus,
    deactivateGmbCampaignStatus,
    setGmbParams,
    setCampaignType,
    setGmbDetailData,
    loadDiagonalGrowthReferralTrafficList,
    setDrtParams,
    createReferralCampaign,
    updateReferralCampaign,
    deleteDrtCampaign,
    loadReferralTrafficDetail,
    loadSingleReferralTrafficDetail,
    activateDrtStatus,
    deactivateDrtStatus,
    getAvailableDates,
  };
});

export const initDiagonalGrowthStore = () => {
  return DiagonalGrowthStore.create({
    websiteDetailData: null,
    gmbDetailData: null,
    gmbKeywordStats: null,
    loadingGmbKeywordStats: false,
    websiteDetailLoading: false,
    keywordPositionHistoryData: null,
    loadingKeywordPositionHistoryData: false,
    keywordDistributionChartData: null,
    loadingKeywordDistributionChartData: false,
    referralTrafficLoading: false,
    referralTrafficDetailLoading: false,
    loadingSingleReferralTrafficDetail: false,
    heatMapLocationsLoader: false,
    createCampaignGmbLoader: false,
    createReferralCampaignLoader: false,
    updateReferralCampaignLoader: false,
    websiteUrl: '',
    campaignType: 'website',
    showCampaignConfigModal: false,
    updateCampaignLoader: false,
    createCampaignLoader: false,
    updateStatusLoading: false,
    diagonalGrowthGmbListLoader: false,
    diagonalGrowthWebsitesListLoader: false,
    gmbDetailLoading: false,
    pageNumber: 1,
    pageSize: 10,
    loadingAvailableDates: false,
  });
};
